import React, { useEffect, useState } from "react";
import { Column, useFilters, useSortBy, useTable } from "react-table";
import SuiteApi from "../../../api/SuiteApi";
import { EImagingStatus, ImagingSchedule } from "../../../Model/ImagingSchedule";
import { EPlateErrorStatus } from "../../../Model/Plate";
import { PlateAddress } from "../../../Model/PlateAddress";
import DateUtil from "../../../Utility/DateUtil";
import "../../Common/CommonTable.css";
import DefaultColumnFilter from "../../Common/DefaultColumnFilter";
import { useDispatch } from "react-redux";
import { removeNotification } from "../../../redux/actions/NotificationAction";

type Props = {
  plateAddress: PlateAddress;
  style?: React.CSSProperties;
};

const initialState: ImagingSchedule[] = [];

export default function PlateInformationSchedule({ plateAddress, style = {} }: Props) {
  const dispatch = useDispatch();
  const [schedules, setSchedules] = useState(initialState);
  const [zeroSchedulesText, setZeroScheduleText] = useState(
    "Loading plate schedules"
  );
  const defaultColumn = React.useMemo(
    () => ({
      // Let's set up our default Filter UI
      Filter: DefaultColumnFilter,
    }),
    []
  );
  const plate = plateAddress.usedByPlate ?? plateAddress.reservedByPlate;

  useEffect(() => {
    if (plate == null) {
      return;
    }
    if (plate.errorStatus === EPlateErrorStatus.OK) {
      SuiteApi.getPlateSchedule(plate.barcode).then((imagingSchedules) => {
        setSchedules(imagingSchedules);
        if (imagingSchedules.length === 0){
          setZeroScheduleText("This plate doesn't have schedule")
        }
      }).catch((error) => {
        dispatch(removeNotification());
        setSchedules(initialState);
        setZeroScheduleText(error);
      });
    }
  }, [plate]);

  const columns: Column<ImagingSchedule>[] = React.useMemo(
    () => [
      {
        Header: "Date",
        id: "date",
        accessor: (row) => {
          return new Date(row.dateToImage).getTime();
        },
      },
      {
        Header: "Scheduled",
        id: "scheduled",
        accessor: (row) => {
          return DateUtil.toUsFormatString(new Date(row.dateToImage));
        },
        width: "40%",
      },
      {
        Header: "Actual",
        id: "actual",
        accessor: (row) => {
          const date = new Date(row.dateImaged);
          if (!DateUtil.isMinDate(date) && (row.state === EImagingStatus.Completed || row.state === EImagingStatus.Cancelled))
          {
            return DateUtil.toUsFormatString(date);
          }
          return EImagingStatus[row.state];
        },
        width: "40%",
      },
      {
        Header: "Priority",
        id: "priority",
        accessor: (row) => row.priority,
        width: "20%",
        Filter: () => (
          <input
            style={{
              width: '90%',
            }}
          />
        ),
      },
    ],
    []
  );

  const instance = useTable<ImagingSchedule>(
    {
      columns,
      data: schedules,
      defaultColumn,
      initialState: {
        sortBy: [
          {
            id: "date",
          },
        ],
        hiddenColumns: ["date"],
      },
    },
    useFilters,
    useSortBy
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = instance;

  if (plate?.errorStatus !== EPlateErrorStatus.OK) {
    return <div>Plate in error state</div>;
  }

  if (schedules.length === 0) {
    return <div>{zeroSchedulesText}</div>;
  }

  return (
    <div className="common-table-container" style={{ ...style }}>
      <table {...getTableProps()} className="common-table">
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th {...column.getHeaderProps({
                  style: { width: column.width },
                })}>
                  <div className="common-table-header">
                    <div className="common-table-header-title" {...column.getSortByToggleProps()}>
                      <svg width="11" height="5" viewBox="0 0 11 5" fill="none" xmlns="http://www.w3.org/2000/svg" />
                      <div>{column.render("Header")}</div>
                      <svg
                        width="11"
                        height="5"
                        viewBox="0 0 11 5"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        transform={column.isSortedDesc ? "rotate(0,0,0)" : "rotate(180,0,0)"}
                      >
                        {column.isSorted && (
                          <path d="M5.8772 5L0.877197 0L10.8772 7.29063e-07L5.8772 5Z" fill="#5C5C5C" />
                        )}
                      </svg>
                    </div>
                    <div className="common-table-header-filter">
                      {column.canFilter ? column.render("Filter") : null}
                    </div>
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map((cell) => {
                  return <td {...cell.getCellProps()}>{cell.render("Cell")}</td>;
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
